import 'events-polyfill';
import ParameterValidator from '@mvps-genesis/parameter-validator';
import {Menu, MenuBar} from './index';
import {getNextSibling} from '@mvps-genesis/selector-helpers';
import {isMouseEvent, isKeyboardEvent} from '@mvps-genesis/event-helpers';

export default class MenuItem {

	static selector = '[role="menuitem"]';

	static fromNodeList (nodeList, options) {
		return Array.from(nodeList).map(node => new MenuItem({
			element: node,
			...options
		}));
	};

	static getInstance (element) {
		return element && element._getMenuItem && element._getMenuItem();
	}

	// eslint-disable-next-line max-lines-per-function
	constructor (options) {

		this._validator = new ParameterValidator(this);

		this._options = this._validator.isObject('options', options);
		this.element = this._validator.isElement('options.element', options.element);
		this.parentOptions = this._validator.isObject('options.parentOptions', options.parentOptions);
		this.parentMenuBar = options.parentMenuBar;
		this.parentMenuBarItem = options.parentMenuBarItem;
		this.parentMenu = options.parentMenu;
		this.subMenu = null;

		// Access instance method
		this.element._getMenuItem = () => this;

		// Bind events
		this.bindEvents();

		// Find subMenus if they exist
		if (this.getAriaHasPopup()) {

			const subMenu = getNextSibling(this.element, `${Menu.selector}, ${MenuBar.selector}`);

			if (subMenu) {

				this.subMenu = subMenu.matches(Menu.selector)
					? new Menu({
						...this.parentOptions,
						element: subMenu,
						parentMenuBarItem: this.parentMenuBar && !this.parentMenu ? this : this.parentMenuBarItem,
						parentMenuItem: this
					})
					: new MenuBar({
						...this.parentOptions,
						element: subMenu,
						parentMenuItem: this
					});

			}

		}

	}

	bindEvents () {

		this.element.addEventListener('keydown', this.onKeyDown);

	}

	destroyEvents () {

		this.element.removeEventListener('keydown', this.onKeyDown);

		if (this.subMenu) {
			this.subMenu.destroyEvents();
		}

	}

	/* eslint-disable no-invalid-this */
	onKeyDown = (e) => {

		let handled = false;

		// Bubble event to parent menu if submenu didn't handle the event
		if (this.parentMenu) {
			handled = this.parentMenu.handleEvent(e, this);
		}

		// Bubble the event to parent menu bar if submenu and parent menu didn't handle the event
		if (this.parentMenuBar && !handled) {
			this.parentMenuBar.handleEvent(e, this);
		}

		return true;

	}
	/* eslint-enable no-invalid-this */

	dispatchEvent (eventName, options) {
		let event = null;
		if (isMouseEvent(eventName)) {
			event = new MouseEvent(eventName, options);
		}
		else if (isKeyboardEvent(eventName)) {
			event = new KeyboardEvent(eventName, options);
		}
		else {
			event = new Event(eventName, options);
		}
		this.element.dispatchEvent(event);
	}

	setTabIndex (n) {
		this.element.tabIndex = n;
	}

	setAriaExpanded (bool) {
		if (this.getAriaHasPopup()) {
			this.element.setAttribute('aria-expanded', bool ? 'true' : 'false');
		}
	}

	getAriaHasPopup () {
		return this.element.getAttribute('aria-haspopup') === 'true';
	}

	getFirstCharacter () {
		return (
			this.element.innerText ||
			this.element.getAttribute('aria-label') ||
			this.element.title ||
			this.element.alt
		).trim().charAt(0).toLowerCase();
	}

	setFocus () {
		this.element.focus();
	}

};
